import { Checkbox, RequiredMark } from '@/components/ui';
import {
  EXPOSED_MALLS,
  EXPOSED_MALL_LABELS,
  type ExposedMall,
} from '../types/bankAccount';

interface ExposedMallSelectorProps {
  /** 현재 선택된 노출상점(BCP-47 다중). */
  value: ExposedMall[];
  onChange: (next: ExposedMall[]) => void;
  disabled?: boolean;
  /** QA-56: 필수 선택(1개 이상)임을 라벨에 `*` 로 표기. */
  required?: boolean;
}

/**
 * 노출상점 다중선택(국문/영문/중문/일문). M-4 ① "노출상점(국문/영문/중문/일문)".
 * 라디오 그룹(RadioRow)과 동일한 라벨 120px + 항목 레이아웃을 따르되 체크박스로
 * 다중선택을 지원한다. 결제 단계에서 접속 상점에 맞는 계좌만 필터 노출(M-4 ④)되므로
 * 한 계좌가 여러 몰에 노출될 수 있다.
 */
export const ExposedMallSelector = ({
  value,
  onChange,
  disabled = false,
  required = false,
}: ExposedMallSelectorProps) => {
  const toggle = (mall: ExposedMall, checked: boolean) => {
    if (checked) {
      if (value.includes(mall)) return;
      // EXPOSED_MALLS 순서를 보존해 일관된 표시 순서를 유지한다.
      onChange(EXPOSED_MALLS.filter((m) => value.includes(m) || m === mall));
    } else {
      onChange(value.filter((m) => m !== mall));
    }
  };

  return (
    <div
      className="grid grid-cols-[120px_1fr] items-center gap-4 py-3"
      style={{ borderBottom: '1px solid var(--divider-soft)' }}
    >
      <span
        className="text-[13px] font-medium"
        style={{ color: 'var(--content-strong)' }}
      >
        노출상점
        {required && <RequiredMark />}
      </span>
      <div
        role="group"
        aria-label="노출상점"
        className="flex flex-wrap items-center gap-5"
      >
        {EXPOSED_MALLS.map((mall) => {
          const id = `exposed-mall-${mall}`;
          return (
            <label
              key={mall}
              htmlFor={id}
              className="inline-flex cursor-pointer items-center gap-2 text-[14px]"
              style={{ color: 'var(--content-default)' }}
            >
              <Checkbox
                id={id}
                checked={value.includes(mall)}
                onCheckedChange={(checked) => toggle(mall, checked === true)}
                disabled={disabled}
                data-testid={`bank-account-mall-${mall}`}
              />
              {EXPOSED_MALL_LABELS[mall]}
            </label>
          );
        })}
      </div>
    </div>
  );
};
